import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PackageCard } from '../components/PackageCard.jsx';
import { fetchCategory } from '../api/store.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useBasket } from '../context/BasketContext.jsx';
import { useToast } from '../context/ToastContext.jsx';

export function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { rankState } = useBasket();
  const { showToast } = useToast();
  const [state, setState] = useState({ status: 'loading', category: null, packages: [] });
  const isEmptyState = state.status !== 'ready' || state.packages.length === 0;

  usePageTitle(
    state.category?.name ?? (state.status === 'loading' ? 'Loading' : 'Category not found'),
  );

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', category: null, packages: [] });

    fetchCategory(slug).then((data) => {
      if (cancelled) return;
      if (data.reason === 'rank_hidden') {
        showToast('This category is hidden for your current Minecraft rank.', 6000, 'info');
        navigate('/home/', { replace: true });
        return;
      }

      setState({
        status: data.category ? 'ready' : 'missing',
        category: data.category ?? null,
        packages: data.packages ?? [],
      });
    });

    return () => {
      cancelled = true;
    };
  }, [slug, rankState, navigate, showToast]);

  return (
    <main className="content">
      <div className={`welcome-card${isEmptyState ? ' category-page-card' : ''}`}>
        <CategoryBody {...state} />
      </div>
    </main>
  );
}

function CategoryBody({ status, category, packages }) {
  if (status === 'loading') {
    return (
      <div className="empty-state category-page-empty">
        <i className="fa-solid fa-spinner fa-spin" />
        <h3>Loading…</h3>
      </div>
    );
  }

  if (status === 'missing') {
    return (
      <div className="empty-state category-page-empty">
        <i className="fa-solid fa-circle-question" />
        <h3>Category not found</h3>
        <p>The category you&apos;re looking for doesn&apos;t exist or has been removed.</p>
      </div>
    );
  }

  return (
    <>
      <div className="section-heading">
        <div>
          <h2>
            <i className="fa-solid fa-layer-group" /> {category.name}
          </h2>
          <div className="section-subtitle">
            {packages.length} package{packages.length === 1 ? '' : 's'} available
          </div>
        </div>
      </div>

      {packages.length === 0 ? (
        <div className="empty-state category-page-empty">
          <i className="fa-solid fa-box-open" />
          <h3>No packages yet</h3>
          <p>Check back soon — new items are added regularly.</p>
        </div>
      ) : (
        <div className="rank-store rank-store--full">
          {packages.map((pkg, index) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              badge={pkg.featured ? 'Popular' : null}
              priority={index < 3}
            />
          ))}
        </div>
      )}
    </>
  );
}
